import React, { useEffect, useMemo, useState } from "react";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { Col } from "@amzn/stencil-react-components/layout";
import { H1, Text } from "../../../components/typography";
import { defaultTextColor } from "../../../config/palette";
import { NewHireVideo, ReferrerMode } from "../../../components/new-hire-video";
import * as adobeAnalytics from "../../../utility/adobe-analytics";
import { Prompt } from "react-router-dom";
import {
  buildUrl,
  VIDEO_RECORD_API_URL,
  VIDEO_RECORD_NON_ASH_API_URL,
} from "../../../config/urls";
import { getSettings } from "../../../helpers/get-settings";
import { fetcher } from "../../../helpers/fetcher";
import { format } from "date-fns";
import { useUrlParams } from "../../../hooks/use-url-params";
import { isDragonstone } from "../../../helpers/url-params";
import useArb from "../../../hooks/use-arb";
import { isEnglishLocale } from "../../../utility/locale-helper";
const URLS = {
  [ReferrerMode.Ash]: VIDEO_RECORD_API_URL,
  [ReferrerMode.NonAsh]: VIDEO_RECORD_NON_ASH_API_URL,
};

export const NewHireVideoRoute = () => {
  let apiEndpoint: string | undefined;

  const {
    referrerMode,
    requisitionId,
    applicationId,
    candidateId,
  } = useUrlParams();

  const videoRecordQueryUrl = useMemo(
    () =>
      buildUrl(URLS[referrerMode], {
        requisitionId,
        applicationId,
        candidateId,
      }),
    [referrerMode, requisitionId, applicationId, candidateId]
  );

  const [watchedTime, setWatchedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [lastPlay, setLastPlay] = useState(0);
  const [videoPlayedAt, setVideoPlayedAt] = useState("");
  const [linkClickedAt, setLinkClickedAt] = useState("");

  const updateWatchedTime = (time: number) => {
    setWatchedTime(time);
  };
  const updateCurrentTime = (time: number) => {
    setCurrentTime(time);
  };
  const updateLastPlay = (time: number) => {
    setLastPlay(time);
  };
  const updateVideoPlayedAt = (time: string) => {
    setVideoPlayedAt(time);
  };

  useEffect(() => {
    setLinkClickedAt(format(new Date(), "yyyy-MM-dd HH:mm:ss"));
  }, []);

  useEffect(() => {
    getSettings().then((settings) => {
      apiEndpoint = settings.apiEndpoint;
    });
  });

  useEffect(() => {
    adobeAnalytics.addPageLoadMetric(window, applicationId, "new hire video", {
      referrer: adobeAnalytics.interpretReferrer(document.referrer),
      jobId: requisitionId,
      isDragonstone: isDragonstone(requisitionId),
    });
  }, [applicationId, requisitionId]);

  useEffect(() => {
    const handleVisibilitychange = async () => {
      if (document.visibilityState !== "visible") {
        await sendDataToServer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilitychange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilitychange);
    };
  });

  useEffect(() => {
    const handlePagehide = async (event: any) => {
      if (event.persisted) {
        await sendDataToServer();
      }
    };
    window.addEventListener("pagehide", handlePagehide);
    return () => {
      window.removeEventListener("pagehide", handlePagehide);
    };
  });
  const bundle = useArb("HVHChecklistPantherResource");

  const sendDataToServer = async () => {
    if (videoPlayedAt === "") {
      return;
    }
    setLastPlay(currentTime);
    localStorage.setItem("videoCurrentTime", String(currentTime));
    const data = JSON.stringify({
      linkClickedAt: linkClickedAt,
      videoPlayedAt: videoPlayedAt,
      totalWatchTime: String(watchedTime),
      language: "en-us",
      applicationId: applicationId,
    });
    setWatchedTime(0);
    setVideoPlayedAt("");
    // Fallback if old browser doesn't support sendBeacon
    if (!navigator.sendBeacon) {
      console.log(
        "The client browser doesn't support Beacon API. https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon#Browser_compatibility"
      );

      navigator.sendBeacon = (url: string, data) => {
        fetcher(url, {
          method: "POST",
          body: data,
        });
        return true;
      };
    }
    navigator.sendBeacon((apiEndpoint ?? "") + videoRecordQueryUrl, data);
  };

  const nheDetails = bundle.getMessage("Checklist-NHE-Details");
  const nheTitle = bundle.getMessage("Checklist-NHE-H1");
  const isEnglish: boolean = isEnglishLocale();
  return (
    <>
      <Prompt
        message={(location: { pathname: string }, action: string) => {
          if (action === "POP") {
            sendDataToServer().catch(() => {
              console.error("Not able to send video stats to server...");
            });
          }
          return true;
        }}
      />
      <PageContainer color={defaultTextColor}>
        <Col gridGap="1rem">
          <H1>{nheTitle}</H1>
          <Text>{nheDetails}</Text>
          <NewHireVideo
            applicationId={applicationId}
            videoRecordQueryUrl={videoRecordQueryUrl}
            watchedTime={watchedTime}
            updateWatchedTime={updateWatchedTime}
            videoPlayedAt={videoPlayedAt}
            updateVideoPlayedAt={updateVideoPlayedAt}
            updateCurrentTime={updateCurrentTime}
            updateLastPlay={updateLastPlay}
            lastPlay={lastPlay}
            isEnglish={isEnglish}
          />
        </Col>
      </PageContainer>
    </>
  );
};
